"use client";
import { useState } from "react";
import { ApiError } from "../../lib/api";
import { Banner, Field, FieldGrid, Input, Textarea } from ".";
import { Choice, CommandForm, ReadState } from "./record-tools";
import { CluePicker, useParticipationOrganizations } from "./participation-tools";
import {
  CLUE_ACTION_LABEL,
  CLUE_LABEL,
  CATEGORIES,
  DOC_TYPES,
  DISPOSITION_LABEL,
  ENDPOINT,
  TOPIC_LABEL,
  localNow,
  type ClueSummary,
  type Editor
} from "./participation-model";

export function ParticipationEditor({
  editor,
  onCancel,
  onSaved
}: {
  editor: Editor;
  onCancel: () => void;
  onSaved: (result: unknown) => void;
}) {
  const organizations = useParticipationOrganizations();
  const source = editor.kind === "topic" ? editor.source : undefined;
  const [initial] = useState(() => ({
    organizationId: source?.organization.id ?? "",
    title: source?.title ?? "",
    category: CATEGORIES[0],
    description: "",
    requirements: "",
    targetLabel: "",
    opensAt: localNow(),
    closesAt: localNow(14),
    year: String(new Date().getFullYear()),
    direction: "",
    leadLabel: "",
    expertLabels: "",
    docType: DOC_TYPES[0],
    summary: "",
    authorLabel: "",
    memberMessage: "",
    note: "",
    disposition: "",
    place: "",
    occurredAt: localNow()
  }));
  const [form, setForm] = useState(initial),
    [checked, setChecked] = useState(false),
    [aiAssisted, setAiAssisted] = useState(false),
    [clue, setClue] = useState<ClueSummary | null>(null),
    [candidateReady, setCandidateReady] = useState(false);
  const set = (key: keyof typeof form, value: string) =>
    setForm((prev) => ({ ...prev, [key]: value }));
  const input = (
    key: keyof typeof form,
    label: string,
    required = false,
    maxLength = 120,
    type = "text",
    minLength = 1
  ) => (
    <Field label={label}>
      <Input
        required={required}
        minLength={required ? minLength : undefined}
        maxLength={maxLength}
        type={type}
        value={form[key]}
        onChange={(e) => set(key, e.target.value)}
        min={key === "year" ? 2000 : undefined}
        max={key === "year" ? 2200 : undefined}
      />
    </Field>
  );
  const area = (
    key: keyof typeof form,
    label: string,
    required = false,
    maxLength = 1000,
    minLength = 4
  ) => (
    <Field label={label}>
      <Textarea
        required={required}
        minLength={required ? minLength : undefined}
        maxLength={maxLength}
        rows={key === "description" || key === "summary" ? 9 : 5}
        value={form[key]}
        onChange={(e) => set(key, e.target.value)}
      />
    </Field>
  );
  const create = editor.action === "create";
  const defaults =
    editor.kind === "clue"
      ? editor.action === "start-review"
        ? "建言已进入受理核对。"
        : editor.action === "accept-for-follow-up"
          ? "建言已进入后续研判；本回执不代表采纳、报送、采用或公开发布。"
          : ""
      : "";
  const needsConfirmation =
    editor.kind === "clue"
      ? editor.action !== "start-review"
      : editor.kind === "call"
        ? true
        : editor.action === "status" || editor.action === "no-response";
  const needsClue =
    (editor.kind === "clue" && editor.action === "merge") ||
    (editor.kind === "topic" && editor.action === "link");
  const title =
    editor.kind === "clue"
      ? CLUE_ACTION_LABEL[editor.action]
      : editor.kind === "call"
        ? { create: "发起材料征集", close: "截止收件", withdraw: "撤回征集" }[editor.action]
        : {
            create: "提出课题",
            status: `课题转为${TOPIC_LABEL[editor.nextStatus!]}`,
            manuscript: "新增稿件版本",
            submission: "生成待报送记录",
            link: "关联来源建言",
            trip: "登记调研记录",
            "no-response": "登记未获反馈"
          }[editor.action];
  const danger =
    (editor.kind === "clue" && ["not-accept", "merge"].includes(editor.action)) ||
    (editor.kind === "call" && editor.action === "withdraw") ||
    (editor.kind === "topic" && editor.action === "status" && editor.nextStatus === "CLOSED");
  const organizationName = organizations.options.find(
    (o) => o.value === form.organizationId
  )?.label;
  const invalidWindow =
    editor.kind === "call" &&
    create &&
    !!form.closesAt &&
    (!Number.isFinite(Date.parse(form.opensAt)) ||
      !Number.isFinite(Date.parse(form.closesAt)) ||
      Date.parse(form.closesAt) <= Date.parse(form.opensAt) ||
      Date.parse(form.closesAt) <= Date.now());
  const disabled =
    invalidWindow ||
    (needsConfirmation && !checked) ||
    (needsClue && (!clue || !candidateReady)) ||
    (create && (!organizations.data || !organizationName)) ||
    (editor.kind === "clue" &&
      ["request-supplement", "not-accept", "merge"].includes(editor.action) &&
      form.memberMessage.trim().length < 4);
  return (
    <CommandForm
      eyebrow="参政议政 · 办理"
      title={title}
      description={
        editor.record
          ? `${editor.record.title} · ${editor.record.organization.name}`
          : "填写材料与办理范围，保存后继续查看进度。"
      }
      dirty={JSON.stringify(form) !== JSON.stringify(initial) || checked || aiAssisted || !!clue}
      onCancel={onCancel}
      onSaved={onSaved}
      danger={danger}
      disabled={disabled}
      label={
        editor.kind === "clue"
          ? "确认办理"
          : editor.kind === "call"
            ? create
              ? "确认发起征集"
              : editor.action === "close"
                ? "确认截止"
                : "确认撤回"
            : editor.action === "submission"
              ? "生成待报送记录"
              : editor.action === "no-response" ? "确认登记未获反馈"
              : editor.action === "status"
                ? "确认变更"
                : "保存登记"
      }
      onSubmit={async (send) => {
        const base = editor.record
          ? `${ENDPOINT[editor.kind]}/${encodeURIComponent(editor.record.id)}`
          : ENDPOINT[editor.kind];
        if (editor.kind === "clue")
          return send(`${base}/${editor.action}`, {
            expectedVersion: editor.record.version,
            reasonCode:
              editor.action === "merge"
                ? "DUPLICATE_OF_ANOTHER_INTAKE"
                : editor.action === "request-supplement"
                  ? "DETAILS_REQUIRED"
                  : editor.action === "not-accept"
                    ? "CURRENT_INTAKE_CLOSED"
                    : "STAFF_REVIEW_DECISION",
            ...(form.memberMessage.trim() ? { memberMessage: form.memberMessage.trim() } : {}),
            ...(editor.action === "merge" ? { mergedIntoId: clue!.id } : {})
          });
        if (editor.kind === "call") {
          if (!create)
            return send(`${base}/${editor.action}`, {
              expectedVersion: editor.record!.version,
              ...(form.note.trim() ? { note: form.note.trim() } : {})
            });
          if (
            form.closesAt &&
            (Date.parse(form.closesAt) <= Date.parse(form.opensAt) ||
              Date.parse(form.closesAt) <= Date.now())
          )
            throw new ApiError(400, "INVALID_WINDOW");
          return send(ENDPOINT.call, {
            organizationId: form.organizationId,
            category: form.category,
            title: form.title.trim(),
            description: form.description.trim(),
            requirements: form.requirements
              .split("\n")
              .map((r) => r.trim())
              .filter(Boolean),
            ...(form.targetLabel.trim() ? { targetLabel: form.targetLabel.trim() } : {}),
            opensAt: new Date(form.opensAt).toISOString(),
            ...(form.closesAt ? { closesAt: new Date(form.closesAt).toISOString() } : {}),
            audienceMode: "ORGANIZATION_SUBTREE",
            audienceOrganizationIds: [form.organizationId]
          });
        }
        if (create)
          return send(ENDPOINT.topic, {
            organizationId: form.organizationId,
            title: form.title.trim(),
            direction: form.direction.trim(),
            year: Number(form.year),
            leadLabel: form.leadLabel.trim(),
            expertLabels: form.expertLabels
              .split(/[,，\n]/)
              .map((v) => v.trim())
              .filter(Boolean),
            ...(source ? { clueSubmissionIds: [source.id] } : {})
          });
        if (editor.action === "status")
          return send(`${base}/status`, {
            expectedVersion: editor.record!.version,
            toStatus: editor.nextStatus,
            ...(editor.nextStatus === "CLOSED" ? { disposition: form.disposition } : {}),
            ...(form.note.trim() ? { note: form.note.trim() } : {})
          });
        if (editor.action === "link")
          return send(`${base}/clue-links`, {
            clueSubmissionId: clue!.id,
            ...(form.note.trim() ? { note: form.note.trim() } : {})
          });
        if (editor.action === "trip")
          return send(`${base}/field-trips`, {
            occurredAt: new Date(form.occurredAt).toISOString(),
            place: form.place.trim(),
            note: form.note.trim()
          });
        if (editor.action === "manuscript")
          return send(`${base}/manuscripts`, {
            title: form.title.trim(),
            docType: form.docType,
            summary: form.summary.trim(),
            authorLabel: form.authorLabel.trim(),
            aiAssisted
          });
        if (editor.action === "no-response") return send(`/api/v1/topics/submissions/${editor.submission!.id}/no-response`, { expectedVersion: editor.submission!.version });
        return send(`${base}/submissions`, {
          manuscriptVersionNumber: editor.versionNumber,
          targetLabel: form.targetLabel.trim(),
          docType: form.docType
        });
      }}
    >
      {create && (
        <section className="record-section">
          <h2>承办与范围</h2>
          <ReadState {...organizations} retry={organizations.reload} />
          <Choice
            label="承办组织"
            value={form.organizationId}
            onChange={(v) => {
              set("organizationId", v);
              setChecked(false);
            }}
            options={organizations.options}
            required
            disabled={!organizations.data}
          />
          {organizations.data && organizations.options.length === 0 && (
            <Banner tone="warning">当前没有可办理的承办组织，请检查参政议政职责的授权范围。</Banner>
          )}
          {editor.kind === "call" && (
            <p className="record-muted">
              面向所选组织及其下级的有效会籍。收件人在发起时确定，人数以发起结果为准。
            </p>
          )}
        </section>
      )}
      {editor.kind === "clue" && (
        <>
          <section className="record-section">
            <h2>办理内容</h2>
            <p className="record-muted">
              当前：{CLUE_LABEL[editor.record.status]} · 收件编号 {editor.record.publicRef}
            </p>
            {editor.action === "merge" && (
              <>
                <Banner tone="warning">
                  当前建言将并入所选事项，停止单独办理。两条记录都保留，归并不能自助撤回。
                </Banner>
                <CluePicker
                  value={clue}
                  exclude={[editor.record.id]}
                  onChange={(v) => {
                    setClue(v);
                    setChecked(false);
                    setCandidateReady(false);
                  }}
                  onReady={setCandidateReady}
                />
              </>
            )}
            {area(
              "memberMessage",
              "会员可见回执说明",
              ["request-supplement", "not-accept", "merge"].includes(editor.action)
            )}
            <p className="record-muted">此说明会向提交会员显示，请写清下一步和可披露的办理理由。</p>
          </section>
          <section className="participation-receipt-preview">
            <span>会员将看到的说明</span>
            <p>{form.memberMessage.trim() || defaults || "填写说明后在此核对。"}</p>
            {editor.action === "merge" && clue && <small>归并去向：{clue.publicRef}</small>}
          </section>
        </>
      )}
      {editor.kind === "call" &&
        (create ? (
          <>
            <section className="record-section">
              <h2>征集内容</h2>
              <Choice
                label="材料体裁"
                value={form.category}
                onChange={(v) => set("category", v)}
                options={CATEGORIES.map((c) => ({ value: c, label: c }))}
                required
              />
              {input("title", "征集标题", true)}
              {area("description", "征集说明", true, 4000, 1)}
              {area("requirements", "材料要求（每行一条）", false, 4000)}
              {input("targetLabel", "报送目标")}
            </section>
            <section className="record-section">
              <h2>收件时间</h2>
              <FieldGrid>
                {input("opensAt", "开始时间", true, 40, "datetime-local")}
                {input("closesAt", "截止时间", false, 40, "datetime-local")}
              </FieldGrid>
              <p className="record-muted">截止时间留空为长期征集。发起后即生成征集及收件人记录。</p>
              {invalidWindow && <Banner tone="danger">截止时间须晚于开始时间和当前时间。</Banner>}
            </section>
          </>
        ) : (
          <section className="record-section">
            <Banner tone="warning">
              {editor.action === "close"
                ? "截止后停止接收新材料，已交材料继续保留。"
                : "撤回后停止本次征集，已交材料和撤回理由继续保留。"}
            </Banner>
            {area("note", "办理说明", editor.action === "withdraw", 500)}
          </section>
        ))}
      {editor.kind === "topic" && (
        <section className="record-section">
          <h2>{create ? "课题内容" : "办理内容"}</h2>
          {create && (
            <>
              {source && (
                <div className="participation-selection">
                  <strong>来源建言：{source.publicRef}</strong>
                  <p>{source.title}</p>
                  <small>将建立来源引用；原始建言与回执继续保留。</small>
                </div>
              )}
              {input("title", "课题名称", true, 120, "text", 4)}
              <FieldGrid>
                {input("year", "年度", true, 4, "number")}
                {input("direction", "研究方向", true, 60)}
                {input("leadLabel", "牵头负责人", true, 60)}
              </FieldGrid>
              {area("expertLabels", "参与专家（每行一人）", false, 1200)}
            </>
          )}
          {editor.action === "status" && (
            <>
              <p>
                从「{TOPIC_LABEL[editor.record!.status]}」转为「{TOPIC_LABEL[editor.nextStatus!]}
                」。
              </p>
              {editor.nextStatus === "CLOSED" && (
                <Choice
                  label="后续处置"
                  value={form.disposition}
                  onChange={(v) => set("disposition", v)}
                  options={Object.entries(DISPOSITION_LABEL).map(([value, label]) => ({
                    value,
                    label
                  }))}
                  required
                />
              )}
              {area("note", "办理说明", editor.nextStatus === "CLOSED")}
            </>
          )}
          {editor.action === "link" && (
            <>
              <CluePicker
                acceptedOnly
                value={clue}
                exclude={editor.record!.clueLinks.map((l) => l.clueSubmissionId)}
                onChange={(v) => {
                  setClue(v);
                  setCandidateReady(false);
                }}
                onReady={setCandidateReady}
              />
              {area("note", "关联说明")}
            </>
          )}
          {editor.action === "trip" && (
            <>
              <FieldGrid>
                {input("occurredAt", "调研时间", true, 40, "datetime-local")}
                {input("place", "调研地点", true)}
              </FieldGrid>
              {area("note", "调研记录", true)}
            </>
          )}
          {editor.action === "manuscript" && (
            <>
              {input("title", "稿件标题", true, 160, "text", 4)}
              <FieldGrid>
                <Choice
                  label="稿件体裁"
                  required
                  value={form.docType}
                  onChange={(v) => set("docType", v)}
                  options={DOC_TYPES.map((c) => ({ value: c, label: c }))}
                />
                {input("authorLabel", "执笔人", true, 60)}
              </FieldGrid>
              {area("summary", "稿件要点", true, 4000)}
              <label className="record-check">
                <input
                  type="checkbox"
                  checked={aiAssisted}
                  onChange={(e) => setAiAssisted(e.target.checked)}
                />
                使用了 AI 辅助整理
              </label>
              <p className="record-muted">保存为新的稿件版本，已有版本继续保留。</p>
            </>
          )}
          {editor.action === "no-response" && <Banner tone="warning">报送对象：{editor.submission?.targetLabel}。截至本次登记仍未获反馈，结果未知；不等于未采用或未通过，也不新增外部结果。</Banner>}
          {editor.action === "submission" && (
            <>
              <div className="participation-selection">
                <strong>报送依据：稿件 v{editor.versionNumber}</strong>
                <p>
                  {
                    editor.record!.manuscripts.find((m) => m.versionNumber === editor.versionNumber)
                      ?.title
                  }
                </p>
              </div>
              {input("targetLabel", "报送目标", true)}
              <Choice
                label="报送体裁"
                required
                value={form.docType}
                onChange={(v) => set("docType", v)}
                options={DOC_TYPES.map((c) => ({ value: c, label: c }))}
              />
              <p className="record-muted">
                保存后生成待报送记录。实际报送与外部反馈由负责人员在移动工作台登记。
              </p>
            </>
          )}
        </section>
      )}
      {needsConfirmation && (
        <label className="record-check">
          <input type="checkbox" checked={checked} onChange={(e) => setChecked(e.target.checked)} />
          {editor.kind === "clue"
            ? "我已核对办理对象、回执说明与上述影响"
            : editor.kind === "call" && create
              ? "我已核对承办组织、收件范围与时间，确认发起征集"
              : "我已核对当前记录与办理结果"}
        </label>
      )}
    </CommandForm>
  );
}
